import { doc, getDoc, runTransaction, updateDoc, arrayUnion, arrayRemove, increment } from 'firebase/firestore';
import { db, auth } from '../firebase/config';

/**
 * Verificar si el usuario actual sigue a otro usuario
 * @param {string} currentUserId - ID del usuario actual
 * @param {string} targetUserId - ID del usuario objetivo
 * @returns {Promise<boolean>}
 */
export const checkIfFollowing = async (currentUserId, targetUserId) => {
  try {
    if (!currentUserId || !targetUserId) {
      return false;
    }

    if (currentUserId === targetUserId) {
      return false;
    }

    const userDoc = await getDoc(doc(db, 'users', currentUserId));
    
    if (!userDoc.exists()) {
      return false;
    }

    const following = userDoc.data()?.following || [];
    return following.includes(targetUserId);
  } catch (error) {
    console.error('Error al verificar si sigue al usuario:', error);
    return false;
  }
};

/**
 * Seguir a un usuario
 * @param {string} targetUserId - ID del usuario a seguir
 * @returns {Promise<Object>}
 */
export const followUser = async (targetUserId) => {
  try {
    const currentUser = auth.currentUser;
    
    if (!currentUser) {
      return {
        success: false,
        error: 'Debes iniciar sesión para seguir usuarios'
      };
    }

    if (currentUser.uid === targetUserId) {
      return {
        success: false,
        error: 'No puedes seguirte a ti mismo'
      };
    }

    // Usar transacción para garantizar atomicidad
    const result = await runTransaction(db, async (transaction) => {
      const currentUserRef = doc(db, 'users', currentUser.uid);
      const targetUserRef = doc(db, 'users', targetUserId);

      const currentUserDoc = await transaction.get(currentUserRef);
      const targetUserDoc = await transaction.get(targetUserRef);

      if (!currentUserDoc.exists()) {
        throw new Error('Tu usuario no existe');
      }

      if (!targetUserDoc.exists()) {
        throw new Error('El usuario que intentas seguir no existe');
      }

      const currentUserData = currentUserDoc.data();
      const following = currentUserData.following || [];

      // Verificar si ya lo sigue
      if (following.includes(targetUserId)) {
        throw new Error('Ya sigues a este usuario');
      }

      // Actualizar usuario actual: agregar a following
      transaction.update(currentUserRef, {
        following: arrayUnion(targetUserId),
        followingCount: increment(1)
      });

      // Actualizar usuario objetivo: agregar a followers
      transaction.update(targetUserRef, {
        followers: arrayUnion(currentUser.uid),
        followersCount: increment(1)
      });

      return { success: true };
    });

    console.log('✅ Usuario seguido exitosamente');

    // Notificación in-app al usuario seguido (fuera de la transacción)
    try {
      const currentUserDoc = await getDoc(doc(db, 'users', currentUser.uid));
      const followerData = currentUserDoc.exists() ? currentUserDoc.data() : {};
      const { createNotification } = await import('./notificationService');
      await createNotification({
        userId: targetUserId,
        type: 'new_follower',
        payload: {
          followerId: currentUser.uid,
          followerName: followerData.displayName || followerData.username || 'Alguien',
          followerPhotoURL: followerData.photoURL || followerData.profileImage || null
        }
      });
    } catch (notifErr) {
      console.warn('No se pudo enviar notificación de nuevo seguidor:', notifErr);
    }

    return result;

  } catch (error) {
    console.error('Error al seguir usuario:', error);
    return {
      success: false,
      error: error.message || 'Error al seguir usuario'
    };
  }
};

/**
 * Dejar de seguir a un usuario
 * @param {string} targetUserId - ID del usuario a dejar de seguir
 * @returns {Promise<Object>}
 */
export const unfollowUser = async (targetUserId) => {
  try {
    const currentUser = auth.currentUser;
    
    if (!currentUser) {
      return {
        success: false,
        error: 'Debes iniciar sesión'
      };
    }

    if (currentUser.uid === targetUserId) {
      return {
        success: false,
        error: 'No puedes dejar de seguirte a ti mismo'
      };
    }

    // Usar transacción para garantizar atomicidad
    const result = await runTransaction(db, async (transaction) => {
      const currentUserRef = doc(db, 'users', currentUser.uid);
      const targetUserRef = doc(db, 'users', targetUserId);

      const currentUserDoc = await transaction.get(currentUserRef);
      const targetUserDoc = await transaction.get(targetUserRef);

      if (!currentUserDoc.exists()) {
        throw new Error('Tu usuario no existe');
      }

      if (!targetUserDoc.exists()) {
        throw new Error('El usuario no existe');
      }

      const currentUserData = currentUserDoc.data();
      const following = currentUserData.following || [];

      // Verificar si realmente lo sigue
      if (!following.includes(targetUserId)) {
        throw new Error('No sigues a este usuario');
      }

      // Actualizar usuario actual: remover de following
      transaction.update(currentUserRef, {
        following: arrayRemove(targetUserId),
        followingCount: increment(-1)
      });

      // Actualizar usuario objetivo: remover de followers
      transaction.update(targetUserRef, {
        followers: arrayRemove(currentUser.uid),
        followersCount: increment(-1)
      });

      return { success: true };
    });

    console.log('✅ Usuario dejado de seguir exitosamente');
    return result;

  } catch (error) {
    console.error('Error al dejar de seguir usuario:', error);
    return {
      success: false,
      error: error.message || 'Error al dejar de seguir usuario'
    };
  }
};

/**
 * Obtener lista de seguidores de un usuario
 * @param {string} userId - ID del usuario
 * @returns {Promise<Object>}
 */
export const getFollowers = async (userId) => {
  try {
    const userDoc = await getDoc(doc(db, 'users', userId));
    
    if (!userDoc.exists()) {
      return {
        success: false,
        error: 'Usuario no encontrado',
        followers: []
      };
    }

    const followers = userDoc.data()?.followers || [];
    
    return {
      success: true,
      followers
    };
  } catch (error) {
    console.error('Error al obtener seguidores:', error);
    return {
      success: false,
      error: error.message,
      followers: []
    };
  }
};

/**
 * Obtener lista de usuarios seguidos
 * @param {string} userId - ID del usuario
 * @returns {Promise<Object>}
 */
export const getFollowing = async (userId) => {
  try {
    const userDoc = await getDoc(doc(db, 'users', userId));
    
    if (!userDoc.exists()) {
      return {
        success: false,
        error: 'Usuario no encontrado',
        following: []
      };
    }

    const following = userDoc.data()?.following || [];
    
    return {
      success: true,
      following
    };
  } catch (error) {
    console.error('Error al obtener usuarios seguidos:', error);
    return {
      success: false,
      error: error.message,
      following: []
    };
  }
};

/**
 * Verificar si el usuario actual tiene bloqueado a otro usuario
 * @param {string} blockerId - ID del usuario que bloquea (currentUser.uid)
 * @param {string} targetUserId - ID del usuario bloqueado
 * @returns {Promise<boolean>}
 */
export const checkIfBlocked = async (blockerId, targetUserId) => {
  try {
    if (!blockerId || !targetUserId) return false;
    const userDoc = await getDoc(doc(db, 'users', blockerId));
    if (!userDoc.exists()) return false;
    const blocked = userDoc.data()?.blocked || [];
    return blocked.includes(targetUserId);
  } catch (error) {
    console.error('Error al verificar bloqueo:', error);
    return false;
  }
};

/**
 * Bloquear a un usuario (añade a la lista blocked del usuario actual)
 * @param {string} targetUserId - ID del usuario a bloquear
 * @returns {Promise<{ success: boolean, error?: string }>}
 */
export const blockUser = async (targetUserId) => {
  try {
    const currentUser = auth.currentUser;
    if (!currentUser) return { success: false, error: 'Debes iniciar sesión' };
    if (currentUser.uid === targetUserId) return { success: false, error: 'No puedes bloquearte a ti mismo' };

    const userRef = doc(db, 'users', currentUser.uid);
    await updateDoc(userRef, { blocked: arrayUnion(targetUserId) });
    return { success: true };
  } catch (error) {
    console.error('Error al bloquear usuario:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Desbloquear a un usuario
 * @param {string} targetUserId - ID del usuario a desbloquear
 * @returns {Promise<{ success: boolean, error?: string }>}
 */
export const unblockUser = async (targetUserId) => {
  try {
    const currentUser = auth.currentUser;
    if (!currentUser) return { success: false, error: 'Debes iniciar sesión' };

    const userRef = doc(db, 'users', currentUser.uid);
    await updateDoc(userRef, { blocked: arrayRemove(targetUserId) });
    return { success: true };
  } catch (error) {
    console.error('Error al desbloquear usuario:', error);
    return { success: false, error: error.message };
  }
};
